import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Trophy,
  Zap,
  Bot,
  Brain,
  Crown,
  Flame,
  Star,
  Target,
  Award,
  Medal,
  Users,
  Coins,
  TrendingUp,
  ShieldCheck,
  Lock,
  Loader2,
  ArrowLeft
} from "lucide-react";
import { API_URL } from "@/config/api";

// Icon mapping
const ICONS = {
  trophy: Trophy,
  zap: Zap,
  bot: Bot,
  brain: Brain,
  crown: Crown,
  flame: Flame,
  star: Star,
  target: Target,
  award: Award,
  medal: Medal,
  users: Users,
  coins: Coins,
  "trending-up": TrendingUp,
  "shield-check": ShieldCheck
};

// Category colors
const CATEGORY_COLORS = {
  bot: { bg: "bg-orange-500/20", text: "text-orange-500", border: "border-orange-500/30" },
  pvp: { bg: "bg-blue-500/20", text: "text-blue-500", border: "border-blue-500/30" },
  rating: { bg: "bg-purple-500/20", text: "text-purple-500", border: "border-purple-500/30" },
  special: { bg: "bg-yellow-500/20", text: "text-yellow-500", border: "border-yellow-500/30" },
  account: { bg: "bg-green-500/20", text: "text-green-500", border: "border-green-500/30" }
};

const AchievementCard = ({ achievement, language }) => {
  const Icon = ICONS[achievement.icon] || Trophy;
  const colors = CATEGORY_COLORS[achievement.category] || CATEGORY_COLORS.special;
  const isUnlocked = achievement.unlocked;
  
  const name = language === "ru" && achievement.name_ru ? achievement.name_ru : achievement.name;
  const description = language === "ru" && achievement.description_ru ? achievement.description_ru : achievement.description;

  return (
    <div 
      className={`
        relative p-4 rounded-lg border-2 transition-all
        ${isUnlocked 
          ? `${colors.bg} ${colors.border}` 
          : "bg-muted/20 border-border/30 opacity-60"
        }
      `}
      data-testid={`achievement-${achievement.id}`}
    >
      {!isUnlocked && (
        <div className="absolute top-2 right-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className={`
          h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0
          ${isUnlocked ? colors.bg : "bg-muted/30"}
        `}>
          <Icon className={`h-6 w-6 ${isUnlocked ? colors.text : "text-muted-foreground"}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className={`font-semibold truncate ${isUnlocked ? "" : "text-muted-foreground"}`}>
              {name}
            </h3>
            <Badge variant="outline" className={`text-xs ${isUnlocked ? colors.text : ""}`}>
              {achievement.points} pts
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default function AchievementsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useStore();
  
  const [loading, setLoading] = useState(true);
  const [achievements, setAchievements] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const res = await fetch(`${API_URL}/api/achievements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAchievements(data);
        
        // Show toast for new achievements
        if (data.new_achievements && data.new_achievements.length > 0) {
          data.new_achievements.forEach(ach => {
            const name = i18n.language === "ru" && ach.name_ru ? ach.name_ru : ach.name;
            toast.success(`🏆 ${t("achievements.unlocked")}: ${name}!`, {
              duration: 5000
            });
          });
        }
      }
    } catch (err) {
      console.error("Failed to load achievements:", err);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const filterAchievements = (list, category) => {
    if (category === "all") return list;
    return list.filter(a => a.category === category);
  };

  const allAchievements = achievements 
    ? [...achievements.unlocked, ...achievements.locked]
    : [];

  const progressPercent = achievements 
    ? Math.round((achievements.total_points / achievements.max_points) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="achievements-page">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate("/profile")}
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        {t("common.back")}
      </Button>

      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-black tracking-tight mb-2">
          {t("achievements.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("achievements.description")}
        </p>
      </div>

      {/* Progress Card */}
      <Card className="glass border-border/50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 rounded-full bg-primary/20 flex items-center justify-center">
                <Trophy className="h-7 w-7 text-primary" />
              </div>
              <div>
                <p className="text-3xl font-bold">{achievements?.total_points || 0}</p>
                <p className="text-sm text-muted-foreground">{t("achievements.points")}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">
                {achievements?.total_unlocked || 0}/{achievements?.total_achievements || 0}
              </p>
              <p className="text-sm text-muted-foreground">{t("achievements.unlocked")}</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>{t("achievements.progress")}</span>
              <span>{progressPercent}%</span>
            </div>
            <Progress value={progressPercent} className="h-3" />
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6 h-auto p-1">
          <TabsTrigger value="all" className="text-xs py-2">
            {t("achievements.categories.all")}
          </TabsTrigger>
          <TabsTrigger value="bot" className="text-xs py-2">
            <Bot className="h-3 w-3 mr-1" />
            {t("achievements.categories.bot")}
          </TabsTrigger>
          <TabsTrigger value="pvp" className="text-xs py-2">
            <Users className="h-3 w-3 mr-1" />
            PvP
          </TabsTrigger>
          <TabsTrigger value="rating" className="text-xs py-2">
            <TrendingUp className="h-3 w-3 mr-1" />
            {t("achievements.categories.rating")}
          </TabsTrigger>
          <TabsTrigger value="special" className="text-xs py-2">
            <Star className="h-3 w-3 mr-1" />
            {t("achievements.categories.special")}
          </TabsTrigger>
          <TabsTrigger value="account" className="text-xs py-2">
            <ShieldCheck className="h-3 w-3 mr-1" />
            {t("achievements.categories.account")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {/* Unlocked */}
          {filterAchievements(achievements?.unlocked || [], activeTab).length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Trophy className="h-5 w-5 text-primary" />
                {t("achievements.unlockedTitle")} ({filterAchievements(achievements?.unlocked || [], activeTab).length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filterAchievements(achievements?.unlocked || [], activeTab).map(ach => (
                  <AchievementCard 
                    key={ach.id} 
                    achievement={ach} 
                    language={i18n.language}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Locked */}
          {filterAchievements(achievements?.locked || [], activeTab).length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-muted-foreground">
                <Lock className="h-5 w-5" />
                {t("achievements.lockedTitle")} ({filterAchievements(achievements?.locked || [], activeTab).length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filterAchievements(achievements?.locked || [], activeTab).map(ach => (
                  <AchievementCard 
                    key={ach.id} 
                    achievement={ach} 
                    language={i18n.language}
                  />
                ))}
              </div>
            </div>
          )}

          {filterAchievements(allAchievements, activeTab).length === 0 && (
            <div className="text-center py-10">
              <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{t("achievements.noAchievements")}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
